import styled from '@emotion/styled';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogContent,
  IconButton,
  ListItemIcon,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { deleteAPI, viewDetailsAPI } from '@src/api/raffle-draw';
import { viewWinnerResultAPI } from '@src/api/rewards-management';
import SvgColor from '@src/components/svg-color';
import { usePartnerDropdownContext } from '@src/context/PartnerDropdown';
import { useToasterContext } from '@src/context/Toaster';
import { AlertType } from '@src/lib/types/toaster';
import moment from 'moment';
import numeral from 'numeral';
import { useMemo, useState } from 'react';
import WarningIcon from '@mui/icons-material/Warning';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';

const icon = (name: string) => <SvgColor src={`/assets/icons/raffle-draw/${name}.svg`} sx={{ width: 20, height: 20 }} />;

const TableRowItem = (props: any) => {
  const { triggerOpen } = useToasterContext();
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [loading3, setLoading3] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const { state: { selectedPartnerId } } = usePartnerDropdownContext();
  
  const bgcolor = useMemo(() => {
    switch(props.status){
      case "ONGOING": 
        return "#5FC365";
      case "SCHEDULED": 
        return "#FBA364";
      case "DEACTIVATED": 
        return "#C1C1C1";
      case "COMPLETED": 
        return "#B5D5FF";
    }
  },[props.status]);


  const onViewWinners = async() => {
    try {
      setLoading(true);
      const result = await viewWinnerResultAPI({ id: props.id, partnerId: selectedPartnerId });

      setLoading(false);
      props.onViewWinners(result);
    } catch (error) {
      setLoading(false);
      triggerOpen({
        type: AlertType.error,
        title: "View Item",
        message: "Failed to view",
      });
    }
  } 

  const onEdit = async() => {
    try {
      setLoading2(true);
      const result = await viewDetailsAPI({ id: props.id, partnerId: selectedPartnerId });
      setLoading2(false);
      props.onOpenEdit(result);
    } catch (error) {
      setLoading2(false);
      triggerOpen({
        type: AlertType.error,
        title: "View Item",
        message: "Failed to view",
      });
    }
  } 

  const onDelete = async() => {
    setShowDeleteConfirm(false);
    try {
      setLoading3(true);
      await deleteAPI({id: props.id});

      setLoading3(false);
      props.refresh();
      triggerOpen({
        title: "Delete Item",
        message: "Deleted successfully",
      });
    } catch (error) {
      setLoading3(false);
      triggerOpen({
        type: AlertType.error,
        title: "Delete Item",
        message: "Failed to delete",
      });
    }
  }

  return (
    <>
    <TableRow hover tabIndex={-1} role="checkbox">
      <TableCell sx={{ width: 100, fontWeight: 700 }} align="left">{props.code}</TableCell>
      <TableCell align="left">{props.description}</TableCell>
      <TableCell align="left">
        {props.type === 'VIP' ? (
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            bgcolor: '#7F22FE',
            color: '#FFFFFF',
            borderRadius: '20px',
            px: 1.25,
            py: 0.4,
            fontSize: 12,
            fontWeight: 700,
          }}>
            <img src="/assets/icons/ic_crown.svg" alt="purchases" style={{ width: 11, height: 11 }} />
            VIP
          </Box>
        ) : (
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            bgcolor: '#F1F5F9',
            color: '#45556C',
            border: "solid 1px #E2E8F0",
            borderRadius: '20px',
            px: 1.25,
            py: 0.4,
            fontSize: 12,
            fontWeight: 500,
          }}>
            <GroupOutlinedIcon sx={{ fontSize: 14 }} />
            Regular
          </Box>
        )}
      </TableCell>
      <TableCell align="left">{numeral(props.totalRedeemedTickets).format("0,000")}</TableCell>
      <TableCell align="left" sx={{ whiteSpace: "pre-line" }}>
        Start: {moment(props.startDate).format("MM-DD-YYYY")}{"\n"}
        End: {moment(props.drawDate).format("MM-DD-YYYY")} | {moment(props.drawTime, "HH:mm").format("hh:mm A")}</TableCell>
      <TableCell sx={{ fontWeight: 700, 
        color: "white" }} 
        align="center">
        <Box sx={{p: 1, bgcolor}}>{props.status?.replace("DEACTIVATED", "INACTIVE")}</Box>
      </TableCell>

      <TableCell align="center" >
        <Stack direction="row"  alignItems={"center"} justifyContent={"center"}>
          <ButtonGroup variant="text" aria-label="Basic button group">
            <Tooltip arrow title={props.status === "ONGOING" ? "Disabled during ongoing draw" : props.status !== "COMPLETED" ? "Available once draw is completed" : "View winners"}>
              <span>
                <LoadingButton
                  disabled={props.status !== "COMPLETED"}
                  loading={loading}
                  onClick={onViewWinners}
                  sx={{color: "#457DBF"}}>
                  {icon("draw")}
                </LoadingButton>
              </span>
            </Tooltip>
            <Tooltip arrow title={props.status === "ONGOING" ? "Disabled during ongoing draw" : props.status === "COMPLETED" ? "Unavailable — draw completed" : "Edit"}>
              <span>
                <LoadingButton
                  loading={loading2}
                  disabled={props.status === "ONGOING" || props.status === "COMPLETED"}
                  onClick={onEdit}
                  sx={{color: "#457DBF"}}>
                  {icon("edit")}
                </LoadingButton>
              </span>
            </Tooltip>
            <Tooltip arrow title={props.status === "ONGOING" ? "Disabled during ongoing draw" : props.status === "COMPLETED" ? "Unavailable — draw completed" : "Delete"}>
              <span>
                <LoadingButton
                  loading={loading3}
                  disabled={props.status === "ONGOING" || props.status === "COMPLETED"}
                  onClick={() => setShowDeleteConfirm(true)}
                  sx={{color: "#B92525"}}>
                  {icon("delete")}
                </LoadingButton>
              </span>
            </Tooltip>
          </ButtonGroup>
        </Stack>
      </TableCell>
    </TableRow>

    <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} PaperProps={{ sx: { borderRadius: 4, px: 2, py: 1, maxWidth: 520 } }}>
      <DialogContent>
        <IconButton onClick={() => setShowDeleteConfirm(false)} sx={{ position: "absolute", top: 2, right: 10 }}>
          <Typography fontSize={18}>✕</Typography>
        </IconButton>
        <Stack alignItems="center" spacing={2} pt={1}>
          <WarningIcon  sx={{width: 55, height: 55, color: "#FFAE00"}}/>
          <Typography fontSize={20} fontWeight={700} color="#FAAC04" textAlign="center">
            Delete raffle draw?
          </Typography>
          <Typography fontSize={14} textAlign="center" color="text.primary">
            This will permanently delete the raffle draw settings and can't be undone.
          </Typography>
          <Stack direction="row" spacing={2} width="100%" pt={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setShowDeleteConfirm(false)}
              sx={{ borderRadius: 1, borderColor: "#ccc", color: "#000", fontWeight: 700, py: 1.5 }}>
              CANCEL
            </Button>
            <LoadingButton
              fullWidth
              loading={loading3}
              variant="contained"
              onClick={onDelete}
              sx={{ borderRadius: 1, bgcolor: "#6A00AC", fontWeight: 700, py: 1.5, "&:hover": { bgcolor: "#580090" } }}>
              Yes, Delete
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
    </>
  );
}

export const StyledNavItemIcon = styled(ListItemIcon)({
  width: 10,
  height: 14,
  padding: 0,
  paddingLeft: 0,
  marginLeft: 0,
  color: 'inherit',
  alignItems: 'center',
  justifyContent: 'center',
});

export default TableRowItem;
